import type { AIModelPricing } from "@x12i/ai-profiles";
import {
  DEFAULT_OPENROUTER_MARKUP_RATE,
  PROFILE_TIERED_CONTEXT_THRESHOLD,
} from "./constants.js";
import type { AiModelPricing, AiUsageInput } from "./types.js";

const TOKENS_PER_UNIT = 1_000_000;

function usdPerToken(perMillion?: number): number {
  return (perMillion ?? 0) / TOKENS_PER_UNIT;
}

function pickInputRatePerMillion(
  pricing: AIModelPricing,
  promptTokens: number,
): number | undefined {
  if (
    pricing.inputBelow200k !== undefined &&
    pricing.inputAbove200k !== undefined
  ) {
    return promptTokens <= PROFILE_TIERED_CONTEXT_THRESHOLD
      ? pricing.inputBelow200k
      : pricing.inputAbove200k;
  }
  return pricing.input ?? pricing.inputBelow200k ?? pricing.inputAbove200k;
}

function pickOutputRatePerMillion(
  pricing: AIModelPricing,
  promptTokens: number,
): number | undefined {
  if (
    pricing.outputBelow200k !== undefined &&
    pricing.outputAbove200k !== undefined
  ) {
    return promptTokens <= PROFILE_TIERED_CONTEXT_THRESHOLD
      ? pricing.outputBelow200k
      : pricing.outputAbove200k;
  }
  return pricing.output ?? pricing.outputBelow200k ?? pricing.outputAbove200k;
}

export type AiProfilesPricingOptions = {
  promptTokens?: number;
  routedViaOpenRouter?: boolean;
};

export function aiProfilesPricingToCatalogPricing(
  pricing: AIModelPricing,
  pricedAt: string,
  options: AiProfilesPricingOptions = {},
): AiModelPricing {
  const promptTokens = options.promptTokens ?? 0;
  const promptUsdPerToken = usdPerToken(pickInputRatePerMillion(pricing, promptTokens));
  const completionUsdPerToken = usdPerToken(
    pickOutputRatePerMillion(pricing, promptTokens),
  );

  const snapshot: AiModelPricing = {
    promptUsdPerToken,
    completionUsdPerToken,
    cacheReadUsdPerToken: usdPerToken(pricing.cachedInput ?? pricing.cacheHit),
    cacheWriteUsdPerToken: usdPerToken(pricing.cacheWrite5m ?? pricing.cacheWrite1h),
    imageUsdPerUnit: 0,
    requestUsdPerRequest: 0,
    pricedAt,
    source: "manual",
  };

  if (options.routedViaOpenRouter) {
    snapshot.openRouterMarkupUsdPerInputToken =
      promptUsdPerToken * DEFAULT_OPENROUTER_MARKUP_RATE;
    snapshot.openRouterMarkupUsdPerOutputToken =
      completionUsdPerToken * DEFAULT_OPENROUTER_MARKUP_RATE;
  }

  return snapshot;
}

export function computeCostFromPricing(
  input: AiUsageInput,
  pricing: AiModelPricing,
  routedViaOpenRouter: boolean,
  includeBreakdown: boolean,
): { cost: number; breakdown?: import("./types.js").AiCostResult["breakdown"] } {
  const { tokens } = input;

  let promptCost = tokens.prompt * pricing.promptUsdPerToken;
  let completionCost = tokens.completion * pricing.completionUsdPerToken;

  const cachingCost =
    (tokens.cacheWrite ?? 0) * (pricing.cacheWriteUsdPerToken ?? 0) +
    (tokens.cached ?? 0) * (pricing.cacheReadUsdPerToken ?? 0);

  const reasoningCost =
    (tokens.reasoning ?? 0) *
    (pricing.reasoningUsdPerToken ?? pricing.promptUsdPerToken);

  const audioCost = (tokens.audio ?? 0) * pricing.promptUsdPerToken;
  const imageCost = (tokens.image ?? 0) * (pricing.imageUsdPerUnit ?? 0);
  const requestFlat = pricing.requestUsdPerRequest;

  if (routedViaOpenRouter) {
    promptCost +=
      tokens.prompt * (pricing.openRouterMarkupUsdPerInputToken ?? 0);
    completionCost +=
      tokens.completion * (pricing.openRouterMarkupUsdPerOutputToken ?? 0);
  }

  const cost =
    promptCost +
    completionCost +
    cachingCost +
    reasoningCost +
    audioCost +
    imageCost +
    requestFlat;

  if (!includeBreakdown) {
    return { cost };
  }

  return {
    cost,
    breakdown: {
      promptCostUsd: promptCost,
      completionCostUsd: completionCost,
      cachingCostUsd: cachingCost || undefined,
      reasoningCostUsd: reasoningCost || undefined,
      audioCostUsd: audioCost || undefined,
      imageCostUsd: imageCost || undefined,
      requestFlatCostUsd: requestFlat || undefined,
    },
  };
}
